import { useLazyQuery, useMutation } from '@apollo/client';
import { graphql } from 'gql.tada';
import type { ResultOf, VariablesOf } from 'gql.tada';

// One charge across many families: the preview says who would be billed and for
// how much, and the mutation writes the drafts. Neither talks to Stripe — the
// drafts are posted from each family's Billing tab, which is where the choice
// between charging a saved method and sending an invoice lives.

const BulkInvoicePreviewQuery = graphql.persisted(
  "BulkInvoicePreview",
  graphql(`
    query BulkInvoicePreview($input: BulkInvoiceInput!) {
      bulkInvoicePreview(input: $input) {
        familyCount
        invoiceCount
        totalCents
        rows {
          familyEnrollmentId
          familyName
          studentNames
          invoiceCount
          totalCents
          action
          message
        }
      }
    }
  `)
);

const CreateBulkInvoicesMutation = graphql.persisted(
  "CreateBulkInvoices",
  graphql(`
    mutation CreateBulkInvoices($input: CreateBulkInvoicesInput!) {
      createBulkInvoices(input: $input) {
        report {
          familyCount
          invoiceCount
          totalCents
          rows {
            familyEnrollmentId
            familyName
            studentNames
            invoiceCount
            totalCents
            scheduleKey
            action
            message
          }
        }
        userErrors {
          message
          path
        }
      }
    }
  `)
);

export type BulkInvoiceInput = VariablesOf<typeof BulkInvoicePreviewQuery>['input'];
export type BulkInvoiceReport = NonNullable<
  ResultOf<typeof BulkInvoicePreviewQuery>['bulkInvoicePreview']
>;
export type BulkInvoiceRow = BulkInvoiceReport['rows'][number];

export { BulkInvoicePreviewQuery, CreateBulkInvoicesMutation };

export function useBulkInvoicePreview() {
  return useLazyQuery(BulkInvoicePreviewQuery, { fetchPolicy: 'network-only' });
}

export function useCreateBulkInvoices() {
  return useMutation(CreateBulkInvoicesMutation);
}
